import { type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useShowAppError } from '@/services/appErrors/appErrorAtom'
import { type CartFragment, type ProductOfferFragment, useCartEntryRemoveMutation } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useTracking } from '@/services/Tracking/useTracking'
import { Button } from 'ui/src/components/Button/Button'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { FullscreenDialog } from 'ui/src/components/Dialog/FullscreenDialog'
import { Text } from 'ui/src/components/Text/Text'
import { Tooltip } from 'ui/src/components/Tooltip/Tooltip'

type RootProps = ComponentProps<typeof Dialog.Root> & {
  shopSessionId: string
  offer: ProductOfferFragment
  onCompleted?: (cart: CartFragment) => void
}

const RemoveCartItemDialogRoot = ({ children, shopSessionId, offer, onCompleted, ...props }: RootProps) => {
  const { t } = useTranslation(['common', 'cart'])
  const showError = useShowAppError()
  const tracking = useTracking()
  const [removeProductOffer, result] = useCartEntryRemoveMutation({
    variables: { shopSessionId: shopSessionId, offerId: offer.id },
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted(data) {
      const updatedShopSession = data.shopSessionCartEntriesRemove.shopSession
      if (updatedShopSession) {
        onCompleted?.(updatedShopSession.cart)
      }
    },
    onError: (error) => {
      logger.error('Failed to remove offer from cart', {
        shopSessionId: shopSessionId,
        offerId: offer.id,
        error,
      })
      showError(error)
    },
  })

  const handleClickRemove = () => {
    tracking.reportDeleteFromCart(offer)
    removeProductOffer()
  }

  return (
    <Dialog.Root {...props}>
      <Tooltip.Root>{children}</Tooltip.Root>

      <FullscreenDialog.Modal
        accessibleCloseLabel={t('common:DIALOG_CLOSE')}
        center={true}
        Footer={
          <>
            <Button size="large" onClick={handleClickRemove} loading={result.loading} fullWidth={true}>
              {t('cart:REMOVE_ENTRY_MODAL_CONFIRM_BUTTON')}
            </Button>
            <Dialog.Close asChild={true}>
              <Button type="button" variant="ghost" size="large" fullWidth={true}>
                {t('cart:REMOVE_ENTRY_MODAL_CANCEL_BUTTON')}
              </Button>
            </Dialog.Close>
          </>
        }
      >
        <Dialog.Title asChild={true}>
          <Text size={{ _: 'md', lg: 'xl' }} align="center">
            {t('cart:REMOVE_ENTRY_MODAL_PROMPT', { name: offer.product.displayNameFull })}
          </Text>
        </Dialog.Title>
      </FullscreenDialog.Modal>
    </Dialog.Root>
  )
}

type TriggerProps = ComponentProps<typeof Dialog.Trigger>
const RemoveCartItemDialogTrigger = ({ children, ...props }: TriggerProps) => {
  const { t } = useTranslation('cart')

  return (
    <>
      <Tooltip.Trigger asChild>
        <Dialog.Trigger asChild {...props} aria-label={t('REMOVE_ENTRY_BUTTON')}>
          {children}
        </Dialog.Trigger>
      </Tooltip.Trigger>

      <Tooltip.Content sideOffset={5}>
        <Text size="xs" color="textNegative" align="center">
          {t('REMOVE_ENTRY_BUTTON')}
        </Text>
      </Tooltip.Content>
    </>
  )
}

export const RemoveCartItemDialog = {
  Root: RemoveCartItemDialogRoot,
  Trigger: RemoveCartItemDialogTrigger,
}
